import React, { useEffect, useMemo, useState } from "react";
import {
  View,
  Pressable,
  ScrollView,
  ActivityIndicator,
  TextInput,
  StyleSheet,
} from "react-native";
import DsModal from "@components/ds/DsModal";
import { t } from "@constants/localization";
import { getDsRoutingDbs } from "@services/api/dsRouting";
import ConfirmPopup from "./ConfirmPopup";
import {
  C,
  RText,
  Tag,
  ChevronRight,
  PeopleIcon,
  Search,
  tpName,
} from "../theme";
import { filterDbs, getCardActions } from "../helpers";

const PATH = "DsRouting/dbSheet";

/**
 * "+ Add DB" / "Change", pixel-matched to the mockup (§10 frames 09–10): a
 * sheet of the CP's delivery boys, nearby on top (server-sorted); tapping
 * one opens "Map this TP to <DB>?"; "Map to DB" assigns. On Change the
 * current DB is marked and "Remove from route" sits under the list.
 * @param tp the card, or null when closed
 * @param handoffFrom DB name the server says holds the boxes (stale list);
 *   the card's own picked lock sets it too. The map confirm then asks the
 *   hand-off question and assigns with confirm_handoff — one popup, no
 *   modal swap (a second Modal opened while this one closes never shows on
 *   Android).
 */
const DbPickerSheet = ({
  tp,
  onClose,
  onAssign,
  onRemove,
  submitting,
  handoffFrom = null,
}) => {
  const [data, setData] = useState(null);
  const [pending, setPending] = useState(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!tp) return undefined;
    let active = true;
    setData(null);
    setPending(null);
    setQuery("");
    setLoading(true);
    getDsRoutingDbs(tp.cx_id)
      .then(({ data: body }) => active && setData(body?.data || null))
      .catch((error) => console.error(error))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [tp]);

  const allDbs = data?.dbs;
  // Local search over the loaded list (name or the phone's shown digits).
  const dbs = useMemo(() => filterDbs(allDbs || [], query), [allDbs, query]);
  const tpTitle = tp ? tpName(tp) : "";
  const currentDbId = data?.current_db_id ?? null;
  const handoffName =
    handoffFrom ||
    (tp && getCardActions(tp).requiresHandoffConfirm ? tp.db?.name : null);
  const handoff = Boolean(handoffName);

  return (
    <DsModal
      visible={Boolean(tp)}
      onClose={onClose}
      scrim={C.scrim}
      contentStyle={styles.sheet}
    >
      <View style={styles.handle} />
      <RText size={17} weight={700}>
        {t(`${PATH}/title`)}
      </RText>
      <RText size={12} weight={500} color={C.muted} style={styles.sub}>
        {tp
          ? t(`${PATH}/subtitle`, { name: tpTitle, area: tp.area || "" })
          : ""}
      </RText>
      {(allDbs || []).length > 0 ? (
        <View style={styles.search}>
          <Search size={16} />
          <TextInput
            testID="ds-routing-db-search"
            value={query}
            onChangeText={setQuery}
            placeholder={t(`${PATH}/searchPlaceholder`)}
            placeholderTextColor={C.muted}
            autoCorrect={false}
            allowFontScaling={false}
            style={styles.searchInput}
          />
        </View>
      ) : null}
      {loading ? (
        <ActivityIndicator color={C.red} style={styles.loader} />
      ) : (
        <ScrollView style={styles.list} contentContainerStyle={styles.rows}>
          {data && !data.live_positions ? (
            <RText size={12} weight={500} color={C.muted}>
              {t(`${PATH}/noPositions`)}
            </RText>
          ) : null}
          {data && dbs.length === 0 ? (
            <RText size={12} weight={500} color={C.muted}>
              {t(`${PATH}/${query ? "noMatch" : "empty"}`)}
            </RText>
          ) : null}
          {dbs.map((db) => {
            const isCurrent = db.cp_db_id === currentDbId;
            return (
              <Pressable
                key={db.cp_db_id}
                testID={`ds-routing-db-${db.cp_db_id}`}
                onPress={() => !isCurrent && setPending(db)}
                style={styles.row}
              >
                <View style={styles.avatar} />
                <View style={styles.grow}>
                  <View style={styles.nameRow}>
                    <RText size={15} weight={700}>
                      {db.name}
                    </RText>
                    {db.nearby ? (
                      <Tag
                        label={t(`${PATH}/nearby`)}
                        bg={C.greenBg}
                        color={C.green}
                        style={styles.nearby}
                      />
                    ) : null}
                    {isCurrent ? (
                      <Tag
                        label={t(`${PATH}/current`)}
                        bg={C.blueBg}
                        color={C.blue}
                        style={styles.nearby}
                      />
                    ) : null}
                  </View>
                  <RText
                    size={12}
                    weight={500}
                    color={C.muted}
                    style={styles.meta}
                  >
                    {[
                      db.phone_masked,
                      t(`${PATH}/tpsToday`, {
                        count: String(db.tps_today),
                      }),
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </RText>
                </View>
                {!isCurrent ? <ChevronRight size={18} /> : null}
              </Pressable>
            );
          })}
          {tp?.db && !tp.db.from_pick ? (
            <Pressable
              testID="ds-routing-remove"
              onPress={() => onRemove(tp)}
              style={styles.remove}
            >
              <RText size={14} weight={600} color={C.red}>
                {t("DsRouting/actions/remove")}
              </RText>
            </Pressable>
          ) : null}
        </ScrollView>
      )}

      <ConfirmPopup
        visible={Boolean(pending)}
        icon={<PeopleIcon />}
        title={
          handoff
            ? t("DsRouting/handoff/assign", {
                current: handoffName,
                next: pending?.name || "",
              })
            : t("DsRouting/mapConfirm/title", { name: pending?.name || "" })
        }
        confirmLabel={t(
          handoff
            ? "DsRouting/handoff/assignConfirm"
            : "DsRouting/mapConfirm/confirm",
        )}
        cancelLabel={t("DsRouting/cancel")}
        loading={submitting}
        onCancel={() => setPending(null)}
        onConfirm={() => onAssign(pending, handoff)}
      >
        {tp && pending ? (
          <View style={styles.mapBox}>
            <View>
              <RText size={14} weight={700}>
                {tpTitle}
              </RText>
              <RText size={12} weight={500} color={C.muted}>
                {[
                  t(`DsRouting/${tp.order_count === 1 ? "order" : "orders"}`, {
                    count: String(tp.order_count ?? 0),
                  }),
                  tp.area,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </RText>
            </View>
            <View style={styles.mapArrow}>
              <ChevronRight size={18} />
            </View>
            <View style={styles.mapDb}>
              <RText size={14} weight={700} color={C.blue}>
                {pending.name}
              </RText>
              <RText size={12} weight={500} color={C.muted}>
                {pending.phone_masked || ""}
              </RText>
            </View>
          </View>
        ) : null}
        <RText size={12} weight={500} color={C.muted}>
          {t("DsRouting/mapConfirm/body", { name: pending?.name || "" })}
        </RText>
      </ConfirmPopup>
    </DsModal>
  );
};

export default DbPickerSheet;

const styles = StyleSheet.create({
  sheet: {
    backgroundColor: C.surface,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingTop: 16,
    paddingHorizontal: 16,
    paddingBottom: 22,
    maxHeight: "85%",
  },
  handle: {
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: C.border,
    alignSelf: "center",
    marginBottom: 14,
  },
  sub: { paddingTop: 3, paddingBottom: 12 },
  search: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    borderWidth: 1,
    borderColor: C.border,
    borderRadius: 10,
    paddingHorizontal: 12,
    marginBottom: 10,
  },
  searchInput: {
    flex: 1,
    paddingVertical: 9,
    fontSize: 14,
    color: C.ink,
  },
  loader: { marginVertical: 24 },
  list: { maxHeight: 460 },
  rows: { gap: 9 },
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    backgroundColor: C.surface,
    borderWidth: 1,
    borderColor: C.border,
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 14,
  },
  avatar: {
    width: 38,
    height: 38,
    borderRadius: 19,
    backgroundColor: C.avatar,
    flexShrink: 0,
  },
  grow: { flex: 1 },
  nameRow: { flexDirection: "row", alignItems: "center", gap: 7 },
  nearby: { paddingVertical: 3, paddingHorizontal: 8 },
  meta: { paddingTop: 2 },
  remove: { alignItems: "center", paddingVertical: 14 },
  mapBox: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    backgroundColor: C.page,
    borderWidth: 1,
    borderColor: C.border,
    borderRadius: 11,
    padding: 12,
    marginBottom: 12,
  },
  mapArrow: { flex: 1, alignItems: "center" },
  mapDb: { alignItems: "flex-end" },
});
